import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Button, Divider, Input } from 'antd';
import ProductFormSection from './ProductFormSection';
import ProductStatusSummary from './ProductStatusSummary';
import VariantListSection from './VariantListSection';
import ProductPropertyLibrarySection from './ProductPropertyLibrarySection';
import ProductPropertyLibraryModal from './ProductPropertyLibraryModal';
import QuickGenerateVariantModal from './QuickGenerateVariantModal';

function slugify(value = '') {
    return `${value}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
}

function emptyVariantDraft() {
    return {
        sku: '',
        name: '',
        barcode: '',
        sale_price: 0,
        status: 'active',
        attributes: {},
        image: '',
        _imageUploading: false,
        shipping: { weight: 0, length: 0, width: 0, height: 0 }
    };
}

function mapAttachmentsToFileList(attachments = []) {
    return (attachments || []).map((item, index) => ({
        uid: `${index}`,
        status: 'done',
        url: item.url,
        type: item.type,
        name: item.name
    }));
}

function createDraftVariantFromCombo(productTitle, combo, existingVariants) {
    const titleSlug = slugify(productTitle || 'product');
    const entries = Object.keys(combo).map((key) => ({ key, value: combo[key] }));
    const valueSlug = entries.map((item) => slugify(item.value)).filter(Boolean).join('-');
    const baseSku = [titleSlug, valueSlug].filter(Boolean).join('-');
    let sku = baseSku || `${titleSlug}-variant`;
    let index = 1;
    const existingSkus = existingVariants.map((item) => item.sku);
    while (existingSkus.indexOf(sku) > -1) {
        sku = `${baseSku}-${String(index).padStart(2, '0')}`;
        index += 1;
    }

    return {
        sku,
        name: `${productTitle || 'Biến thể'} ${index === 1 ? '' : index}`.trim(),
        barcode: '',
        sale_price: 0,
        status: 'active',
        attributes: combo,
        image: '',
        _imageUploading: false,
        shipping: { weight: 0, length: 0, width: 0, height: 0 },
        _isDraft: true
    };
}

function extractPropertyNamesFromVariants(variants = []) {
    return Array.from(new Set(
        (variants || []).reduce((acc, variant) => acc.concat(Object.keys(variant.attributes || {})), [])
            .map((item) => `${item || ''}`.trim())
            .filter(Boolean)
    ));
}

function extractPropertyValuesFromVariants(variants = []) {
    return (variants || []).reduce((acc, variant) => {
        Object.keys(variant.attributes || {}).forEach((name) => {
            const finalName = `${name || ''}`.trim();
            const finalValue = `${variant.attributes[name] || ''}`.trim();
            if (!finalName || !finalValue) return;
            if (!acc[finalName]) acc[finalName] = [];
            if (acc[finalName].indexOf(finalValue) === -1) {
                acc[finalName] = acc[finalName].concat(finalValue);
            }
        });
        return acc;
    }, {});
}

export default function ProductDrawer(props) {
    const { visible, onClose, product, categories = [], onSaveProduct, fileList = [], onFileChange } = props;
    const [form] = Form.useForm();
    const [variantList, setVariantList] = useState([]);
    const [saving, setSaving] = useState(false);
    const [propertyLibrary, setPropertyLibrary] = useState([]);
    const [selectedPropertyNames, setSelectedPropertyNames] = useState([]);
    const [selectedPropertyValuesMap, setSelectedPropertyValuesMap] = useState({});
    const [createPropertyName, setCreatePropertyName] = useState('');
    const [propertyLibraryVisible, setPropertyLibraryVisible] = useState(false);
    const [quickGenerateVisible, setQuickGenerateVisible] = useState(false);
    const [quickGenerateSelection, setQuickGenerateSelection] = useState({});

    const normalizePropertyValues = (values = []) => Array.from(
        new Set((values || []).map((item) => `${item || ''}`.trim()).filter(Boolean))
    );

    const upsertPropertyLibraryItem = (propertyName, values = []) => {
        const finalName = `${propertyName || ''}`.trim();
        if (!finalName) return;

        setPropertyLibrary((current) => {
            const index = current.findIndex((item) => item.name === finalName);
            const nextItem = index > -1
                ? { ...current[index], values: normalizePropertyValues(values) }
                : { name: finalName, values: normalizePropertyValues(values) };

            if (index === -1) {
                return [nextItem].concat(current);
            }

            const next = current.slice();
            next[index] = nextItem;
            return next;
        });
    };

    const createOrUpdateProperty = async (name, values = []) => {
        const finalName = `${name || ''}`.trim();
        if (!finalName) return null;
        const payload = {
            name: finalName,
            values: Array.from(new Set((values || []).map((item) => `${item || ''}`.trim()).filter(Boolean)))
        };
        const response = await props.fetchData({
            url: 'api/v1/product/properties',
            method: 'post',
            data: payload
        });
        return response.data;
    };

    const updateProperty = async (propertyId, payload = {}) => {
        const response = await props.fetchData({
            url: `api/v1/product/properties/${propertyId}`,
            method: 'put',
            data: {
                ...payload,
                ...(Object.prototype.hasOwnProperty.call(payload, 'name') ? { name: `${payload.name || ''}`.trim() } : {}),
                ...(Object.prototype.hasOwnProperty.call(payload, 'values') ? { values: normalizePropertyValues(payload.values) } : {})
            }
        });
        return response.data;
    };

    const removeProperty = async (propertyId) => {
        return props.fetchData({
            url: `api/v1/product/properties/${propertyId}`,
            method: 'delete'
        });
    };

    const currentSummaryProduct = useMemo(() => ({
        ...product,
        variants: variantList,
        variant_count: variantList.length,
        active_variant_count: variantList.filter((item) => item.status === 'active').length,
        has_variants: variantList.length > 0,
        min_price: variantList.length > 0 ? Math.min(...variantList.map((item) => Number(item.sale_price || 0))) : 0,
        max_price: variantList.length > 0 ? Math.max(...variantList.map((item) => Number(item.sale_price || 0))) : 0
    }), [product, variantList]);

    const selectedProperties = useMemo(() => {
        const selectedSet = new Set(selectedPropertyNames);
        return propertyLibrary
            .filter((item) => selectedSet.has(item.name))
            .map((item) => ({
                ...item,
                allValues: item.values || [],
                values: Object.prototype.hasOwnProperty.call(selectedPropertyValuesMap, item.name)
                    ? selectedPropertyValuesMap[item.name]
                    : (item.values || [])
            }));
    }, [propertyLibrary, selectedPropertyNames, selectedPropertyValuesMap]);

    const loadVariants = async (productId) => {
        if (!productId) {
            setVariantList([]);
            return;
        }

        try {
            let page = 1;
            const limit = 200;
            let allVariants = [];
            while (true) {
                const response = await props.fetchData({
                    url: `api/v1/product/variants?product_id=${productId}&limit=${limit}&page=${page}`
                });
                const batch = response.data || [];
                allVariants = allVariants.concat(batch);
                if (batch.length < limit) break;
                page += 1;
            }
            setVariantList(allVariants);
            setSelectedPropertyNames(extractPropertyNamesFromVariants(allVariants));
            setSelectedPropertyValuesMap(extractPropertyValuesFromVariants(allVariants));
        } catch (err) {
            props.notification({ title: 'Variant', content: err.message || 'Không thể tải danh sách variant', type: 'error' });
        }
    };

    const loadProperties = async () => {
        try {
            const response = await props.fetchData({ url: 'api/v1/product/properties' });
            const nextProperties = response.data || [];
            setPropertyLibrary(nextProperties);
        } catch (err) {
            props.notification({ title: 'Property Library', content: err.message || 'Không thể tải property library', type: 'error' });
        }
    };

    useEffect(() => {
        if (visible && product) {
            form.setFieldsValue({
                _id: product._id,
                title: product.title,
                category_id: product.category_id,
                description: product.description,
                status: product.status === 'active'
            });
            onFileChange({ fileList: mapAttachmentsToFileList(product.attachments || []) });
            loadVariants(product._id);
            loadProperties();
        }
        if (visible && !product) {
            form.resetFields();
            form.setFieldsValue({ status: true });
            onFileChange({ fileList: [] });
            setVariantList([]);
            setSelectedPropertyNames([]);
            setSelectedPropertyValuesMap({});
            loadProperties();
        }
    }, [visible, product]);

    useEffect(() => {
        setCreatePropertyName('');
        setPropertyLibraryVisible(false);
        setQuickGenerateSelection({});
    }, [visible, product]);

    useEffect(() => {
        setSelectedPropertyValuesMap((current) => selectedPropertyNames.reduce((acc, propertyName) => {
            const property = propertyLibrary.find((item) => item.name === propertyName);
            const propertyValues = property ? property.values || [] : [];

            if (Object.prototype.hasOwnProperty.call(current, propertyName)) {
                acc[propertyName] = propertyValues.length > 0
                    ? current[propertyName].filter((value) => propertyValues.indexOf(value) > -1)
                    : current[propertyName];
                return acc;
            }

            acc[propertyName] = propertyValues;
            return acc;
        }, {}));
    }, [selectedPropertyNames, propertyLibrary]);

    useEffect(() => {
        setQuickGenerateSelection((current) => Object.keys(current).reduce((acc, name) => {
            if (selectedPropertyNames.indexOf(name) > -1) {
                acc[name] = current[name];
            }
            return acc;
        }, {}));
    }, [selectedPropertyNames]);

    const handleCreateProperty = async () => {
        const finalName = `${createPropertyName || ''}`.trim();
        if (!finalName) return;

        try {
            const existingProperty = propertyLibrary.find((item) => item.name === finalName);
            const created = await createOrUpdateProperty(finalName, existingProperty ? existingProperty.values || [] : []);
            if (!created) return;
            upsertPropertyLibraryItem(created.name, created.values || []);
            setCreatePropertyName('');
            setSelectedPropertyNames((current) => current.indexOf(created.name) > -1 ? current : current.concat(created.name));
            setSelectedPropertyValuesMap((current) => ({
                ...current,
                [created.name]: created.values || []
            }));
        } catch (err) {
            props.notification({ title: 'Property Library', content: err.message || 'Không thể tạo thuộc tính', type: 'error' });
        }
    };

    const handleUpdateProperty = async (propertyId, payload = {}) => {
        const currentProperty = propertyLibrary.find((item) => `${item._id}` === `${propertyId}`);
        if (!currentProperty) return null;

        const nextName = Object.prototype.hasOwnProperty.call(payload, 'name') ? `${payload.name || ''}`.trim() : currentProperty.name;
        const nextValues = Object.prototype.hasOwnProperty.call(payload, 'values')
            ? normalizePropertyValues(payload.values)
            : currentProperty.values || [];

        setPropertyLibrary((current) => current.map((item) => (
            `${item._id}` === `${propertyId}` ? { ...item, name: nextName, values: nextValues } : item
        )));

        if (currentProperty.name !== nextName) {
            setSelectedPropertyNames((current) => current.map((item) => (item === currentProperty.name ? nextName : item)));
            setSelectedPropertyValuesMap((current) => {
                if (!Object.prototype.hasOwnProperty.call(current, currentProperty.name)) return current;
                const next = { ...current };
                next[nextName] = next[currentProperty.name];
                delete next[currentProperty.name];
                return next;
            });
            setQuickGenerateSelection((current) => {
                if (!Object.prototype.hasOwnProperty.call(current, currentProperty.name)) return current;
                const next = { ...current };
                next[nextName] = next[currentProperty.name];
                delete next[currentProperty.name];
                return next;
            });
        }

        try {
            const savedProperty = await updateProperty(propertyId, { name: nextName, values: nextValues });
            if (!savedProperty) return null;
            setPropertyLibrary((current) => current.map((item) => (
                `${item._id}` === `${propertyId}` ? savedProperty : item
            )));
            return savedProperty;
        } catch (err) {
            await loadProperties();
            props.notification({ title: 'Property Library', content: err.message || 'Không thể cập nhật thuộc tính', type: 'error' });
            return null;
        }
    };

    const handleDeleteProperty = async (property) => {
        if (!property || !property._id) return;

        const propertyName = property.name;
        setPropertyLibrary((current) => current.filter((item) => `${item._id}` !== `${property._id}`));
        setSelectedPropertyNames((current) => current.filter((item) => item !== propertyName));
        setSelectedPropertyValuesMap((current) => {
            const next = { ...current };
            delete next[propertyName];
            return next;
        });
        setQuickGenerateSelection((current) => {
            const next = { ...current };
            delete next[propertyName];
            return next;
        });

        try {
            await removeProperty(property._id);
        } catch (err) {
            await loadProperties();
            props.notification({ title: 'Property Library', content: err.message || 'Không thể xóa thuộc tính', type: 'error' });
        }
    };

    const handleOpenQuickGenerate = () => {
        if (selectedProperties.length === 0) {
            props.notification({ title: 'Tạo nhanh biến thể', content: 'Chọn ít nhất một thuộc tính trước khi tạo nhanh variant', type: 'error' });
            return;
        }
        setQuickGenerateSelection(
            selectedProperties.reduce((acc, property) => ({
                ...acc,
                [property.name]: property.values || []
            }), {})
        );
        setQuickGenerateVisible(true);
    };

    const handleSelectedPropertyNamesChange = (names = []) => {
        setSelectedPropertyNames(names);
    };

    const handleSelectedPropertyValuesChange = (propertyName, values = []) => {
        setSelectedPropertyValuesMap((current) => ({
            ...current,
            [propertyName]: normalizePropertyValues(values)
        }));
    };

    const handleQuickGenerateSelectionChange = (propertyName, values) => {
        setQuickGenerateSelection((current) => ({
            ...current,
            [propertyName]: values
        }));
    };

    const handleQuickGenerate = async () => {
        try {
            const entries = Object.keys(quickGenerateSelection)
                .map((name) => ({ name, values: quickGenerateSelection[name] || [] }))
                .filter((item) => item.values.length > 0);
            if (entries.length === 0) {
                setQuickGenerateVisible(false);
                return;
            }

            const arrays = entries.map((entry) => entry.values.map((value) => ({ name: entry.name, value })));
            const combos = arrays.reduce((acc, current) => {
                if (acc.length === 0) return current.map((item) => ({ [item.name]: item.value }));
                const next = [];
                acc.forEach((base) => {
                    current.forEach((item) => {
                        next.push({ ...base, [item.name]: item.value });
                    });
                });
                return next;
            }, []);

            const title = form.getFieldValue('title') || (product && product.title) || 'product';
            const nextVariants = combos.reduce((acc, combo) => {
                const draft = createDraftVariantFromCombo(title, combo, acc);
                const duplicate = acc.find((item) => item.sku === draft.sku);
                if (duplicate) return acc;
                return acc.concat([draft]);
            }, variantList.slice());

            setVariantList(nextVariants);
            setQuickGenerateVisible(false);
        } catch (err) {
            props.notification({ title: 'Tạo nhanh biến thể', content: err.message || 'Không thể tạo nhanh biến thể', type: 'error' });
        }
    };

    const handleVariantChange = (index, nextVariant) => {
        const nextList = variantList.slice();
        nextList[index] = nextVariant;
        setVariantList(nextList);
    };

    const handleCreateVariant = () => {
        setVariantList(variantList.concat([emptyVariantDraft()]));
    };

    const handleSaveAll = async (values) => {
        try {
            setSaving(true);
            await onSaveProduct(values, variantList);
            setSaving(false);
        } catch (err) {
            setSaving(false);
        }
    };

    return (
        <Drawer title={product ? 'Cập nhật sản phẩm' : 'Thêm sản phẩm'} placement="right" width={960} onClose={onClose} visible={visible} destroyOnClose>
            <Form form={form} layout="vertical" onFinish={handleSaveAll}>
                <Form.Item name="_id" hidden><Input /></Form.Item>
                <ProductFormSection categories={categories} fileList={fileList} onFileChange={onFileChange} />
                <ProductStatusSummary product={currentSummaryProduct} />
                <Divider />
                <ProductPropertyLibrarySection
                    properties={selectedProperties}
                    selectedPropertyNames={selectedPropertyNames}
                    allProperties={propertyLibrary}
                    onSelectedPropertyNamesChange={handleSelectedPropertyNamesChange}
                    onSelectedPropertyValuesChange={handleSelectedPropertyValuesChange}
                    onOpenPropertyLibrary={() => setPropertyLibraryVisible(true)}
                    onOpenQuickGenerate={handleOpenQuickGenerate}
                />
                <VariantListSection
                    variants={variantList}
                    onCreateVariant={handleCreateVariant}
                    onVariantChange={handleVariantChange}
                    onRemoveVariant={(index) => setVariantList(variantList.filter((_, idx) => idx !== index))}
                />
                <Divider />
                <div className="d-flex justify-content-end">
                    <Button onClick={onClose} style={{ marginRight: 8 }}>Đóng</Button>
                    <Button type="primary" htmlType="submit" loading={saving}>Lưu sản phẩm</Button>
                </div>
            </Form>
            <ProductPropertyLibraryModal
                visible={propertyLibraryVisible}
                onClose={() => {
                    setPropertyLibraryVisible(false);
                    setCreatePropertyName('');
                }}
                properties={propertyLibrary}
                createPropertyName={createPropertyName}
                onCreatePropertyNameChange={setCreatePropertyName}
                onCreateProperty={handleCreateProperty}
                onUpdateProperty={handleUpdateProperty}
                onDeleteProperty={handleDeleteProperty}
            />
            <QuickGenerateVariantModal
                visible={quickGenerateVisible}
                onClose={() => setQuickGenerateVisible(false)}
                properties={selectedProperties}
                selectedProperties={quickGenerateSelection}
                onSelectValues={handleQuickGenerateSelectionChange}
                onConfirm={handleQuickGenerate}
            />
        </Drawer>
    );
}
